<template>
  <div class="workflow-page">
    <!-- Header with subtitle + stats -->
    <div class="workflow-header">
      <div class="header-left">
        <h1 class="page-title">
          <i class="fa fa-sitemap page-title-icon"></i>
          Workflows
        </h1>
        <p class="page-subtitle">
          Automate repetitive tasks &mdash; send reminders, assign follow-ups, and more when events happen in your practice.
        </p>
        <!-- Stat pills -->
        <div class="stat-pills" v-if="workflows.length > 0">
          <button
            class="stat-pill"
            :class="{ active: statusFilter === 'active' }"
            @click="toggleStatusFilter('active')"
          >
            <span class="stat-dot stat-dot--active"></span>
            {{ activeCount }} Active
          </button>
          <button
            class="stat-pill"
            :class="{ active: statusFilter === 'inactive' }"
            @click="toggleStatusFilter('inactive')"
          >
            <span class="stat-dot stat-dot--inactive"></span>
            {{ inactiveCount }} Paused
          </button>
          <button
            class="stat-pill"
            :class="{ active: statusFilter === null }"
            @click="toggleStatusFilter(null)"
          >
            {{ workflows.length }} Total
          </button>
        </div>
      </div>
      <div class="header-actions">
        <b-button v-if="$can('update', 'workflow')" variant="success" class="create-btn" @click="showCreateModal">
          <i class="fa fa-plus" aria-hidden="true"></i> New Workflow
        </b-button>
      </div>
    </div>

    <WorkflowListSkeleton v-if="showOverlay" />

    <!-- Filter Row (only when workflows exist) -->
    <div class="filter-bar" v-if="workflows.length > 0" v-show="!showOverlay">
      <div class="search-box">
        <i class="fa fa-search search-icon"></i>
        <input
          type="text"
          v-model="searchTerm"
          placeholder="Search workflows..."
          class="search-input"
          @input="onSearchInput"
        />
        <i
          v-if="searchTerm"
          class="fa fa-times-circle clear-icon"
          @click="clearSearch"
          title="Clear search"
        ></i>
      </div>
      <div class="tag-filters" v-if="availableTriggers.length > 1">
        <a
          href="#"
          class="tag-filter-btn"
          :class="{ 'active': !selectedTrigger }"
          @click.prevent="selectedTrigger = null"
        >All</a>
        <a
          v-for="trigger in availableTriggers"
          :key="trigger"
          href="#"
          class="tag-filter-btn"
          :class="{ 'active': selectedTrigger === trigger }"
          @click.prevent="selectedTrigger = trigger"
        >{{ getTriggerLabel(trigger) }}</a>
      </div>
    </div>

    <!-- Empty state (no workflows at all) -->
    <div class="empty-state" v-if="workflows.length === 0 && !showOverlay" v-show="!showOverlay">
      <div class="empty-state-inner">
        <div class="workflow-explainer">
          <h3 class="explainer-title">
            <i class="fa fa-magic"></i>
            Automate Your Practice
          </h3>
          <p class="explainer-description">
            Workflows connect <strong>triggers</strong> (events in your practice)
            to <strong>actions</strong> (things that happen automatically).
            Save time and never miss a follow-up!
          </p>
          <div class="explainer-examples">
            <div class="example-item">
              <i class="fa fa-calendar-check-o"></i>
              <span>Send appointment reminders automatically</span>
            </div>
            <div class="example-item">
              <i class="fa fa-file-text-o"></i>
              <span>Email intake forms to new patients</span>
            </div>
            <div class="example-item">
              <i class="fa fa-tasks"></i>
              <span>Create follow-up tasks for your team</span>
            </div>
          </div>
        </div>

        <WorkflowPreviewIllustration type="html" :showCaption="false" />

        <!-- Inline template cards for empty state -->
        <div class="empty-template-grid">
          <div
            v-for="template in workflowTemplates"
            :key="template.id"
            class="suggestion-card"
            :class="{ disabled: !template.triggerEnabled }"
            @click="template.triggerEnabled && createFromTemplateDirectly(template)"
          >
            <div class="suggestion-icon" :style="{ backgroundColor: template.color + '15' }">
              <i :class="template.icon" :style="{ color: template.color }"></i>
            </div>
            <div class="suggestion-content">
              <span class="suggestion-name">{{ template.name }}</span>
              <span class="suggestion-desc">{{ template.description }}</span>
            </div>
            <span v-if="!template.triggerEnabled" class="coming-soon-badge">Coming Soon</span>
            <span v-else class="create-badge">+ Create</span>
          </div>
        </div>

        <div v-if="$can('update', 'workflow')" class="empty-actions">
          <b-button variant="success" class="create-btn-empty" @click="showCreateModal">
            <i class="fa fa-plus" aria-hidden="true"></i> Create Your First Workflow
          </b-button>
          <a href="#" class="scratch-link" @click.prevent="gotoCreatePage()">or start from scratch</a>
        </div>
      </div>
    </div>

    <!-- Search/filter empty state -->
    <div class="no-results" v-if="workflows.length > 0 && filteredWorkflows.length === 0 && !showOverlay" v-show="!showOverlay">
      <i class="fa fa-search no-results-icon"></i>
      <p v-if="searchTerm">No workflows match "{{ searchTerm }}"</p>
      <p v-else-if="selectedTrigger">No workflows with trigger "{{ getTriggerLabel(selectedTrigger) }}"</p>
      <p v-else-if="statusFilter">No {{ statusFilter }} workflows</p>
    </div>

    <!-- Workflow Cards Grid -->
    <div class="workflow-cards-grid" v-if="filteredWorkflows.length > 0" v-show="!showOverlay">
      <div
        class="workflow-card"
        v-for="workflow in paginatedWorkflows"
        :key="workflow.WorkflowID"
        :class="{ inactive: !workflow.Active, 'no-edit': !canEditWorkflow }"
        @click="canEditWorkflow && gotoViewPage(workflow.WorkflowID)"
      >
        <!-- Card header -->
        <div class="card-top">
          <div class="trigger-icon-wrap" :style="{ backgroundColor: getTriggerColor(workflow.TriggerOn) + '15' }">
            <i :class="'fa ' + getTriggerIcon(workflow.TriggerOn)" :style="{ color: getTriggerColor(workflow.TriggerOn) }"></i>
          </div>
          <div class="card-title-area">
            <span class="card-name" v-html="workflow.Name"></span>
            <span class="card-flow-summary">{{ getFlowSummary(workflow) }}</span>
          </div>
          <div class="card-status" @click.stop>
            <label class="toggle-switch">
              <input
                type="checkbox"
                v-model="workflow.Active"
                :disabled="!canEditWorkflow || processingWorkflowId === workflow.WorkflowID"
              >
              <span class="toggle-slider"></span>
            </label>
            <span class="status-label" :class="{ active: workflow.Active }">
              {{ workflow.Active ? 'Active' : 'Paused' }}
            </span>
          </div>
        </div>

        <!-- Card description -->
        <p class="card-description">{{ getWorkflowDescription(workflow) }}</p>

        <!-- Card footer -->
        <div class="card-footer">
          <span class="card-date">Updated {{ workflow.LastUpdated | dateformat(dateFormatStr) }}</span>
          <div class="card-actions" @click.stop>
            <a
              href="#"
              :id="`output-workflow-${workflow.WorkflowID}`"
              class="action-button action-button--primary"
              @click.prevent="gotoWorkflowOutputPage(workflow.WorkflowID)"
              title="See what this automation has done"
            >
              <i class="fa fa-external-link"></i>
              <span>View Activity</span>
            </a>
            <a
              v-if="$can('update', 'workflow')"
              href="#"
              :id="`edit-workflow-${workflow.WorkflowID}`"
              class="action-icon"
              @click.prevent="gotoViewPage(workflow.WorkflowID)"
              title="Edit"
            >
              <i class="fa fa-pencil"></i>
            </a>
            <a
              v-if="$can('delete', 'workflow')"
              href="#"
              :id="`delete-workflow-${workflow.WorkflowID}`"
              class="action-icon action-icon--danger"
              @click.prevent="showModal('delete', workflow.WorkflowID)"
              title="Delete"
            >
              <i class="fa fa-trash"></i>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination-container" v-if="totalPages > 1 || totalItems > 10" v-show="!showOverlay">
      <div class="pagination-info">
        Showing {{ startItem }}-{{ endItem }} of {{ totalItems }}
      </div>
      <div class="pagination-controls">
        <button class="pagination-btn" :disabled="currentPage === 1" @click="currentPage = 1">
          <i class="fa fa-angle-double-left"></i>
        </button>
        <button class="pagination-btn" :disabled="currentPage === 1" @click="previousPage">
          <i class="fa fa-angle-left"></i>
        </button>
        <span class="pagination-page">Page {{ currentPage }} of {{ totalPages }}</span>
        <button class="pagination-btn" :disabled="currentPage === totalPages" @click="nextPage">
          <i class="fa fa-angle-right"></i>
        </button>
        <button class="pagination-btn" :disabled="currentPage === totalPages" @click="currentPage = totalPages">
          <i class="fa fa-angle-double-right"></i>
        </button>
      </div>
    </div>

    <!-- Suggested Workflows Section (collapsible) -->
    <div class="suggestions-section" v-if="workflows.length > 0 && unusedTemplates.length > 0" v-show="!showOverlay">
      <div class="suggestions-header" @click="toggleSuggestions">
        <h5 class="suggestions-title">
          <i class="fa fa-lightbulb-o"></i>
          Suggested Workflows
        </h5>
        <i class="fa" :class="suggestionsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
      </div>
      <div class="suggestions-grid" v-if="suggestionsExpanded">
        <div
          v-for="template in unusedTemplates"
          :key="template.id"
          class="suggestion-card suggestion-card--ghost"
          :class="{ disabled: !template.triggerEnabled }"
          @click="template.triggerEnabled && createFromTemplateDirectly(template)"
        >
          <div class="suggestion-icon" :style="{ backgroundColor: template.color + '15' }">
            <i :class="template.icon" :style="{ color: template.color }"></i>
          </div>
          <div class="suggestion-content">
            <span class="suggestion-name">{{ template.name }}</span>
            <span class="suggestion-desc">{{ template.description }}</span>
          </div>
          <span v-if="!template.triggerEnabled" class="coming-soon-badge">Coming Soon</span>
          <span v-else class="create-badge">+ Create</span>
        </div>
      </div>
    </div>

    <!-- Create Workflow Modal (kept as fallback for + New Workflow button) -->
    <b-modal ref="createWorkflowModal" id="createWorkflowModal" title="Create New Workflow" body-class="bg-white" centered size="lg">
      <div class="templates-section mb-4">
        <label class="templates-label mb-2">Quick Start from Template</label>
        <div class="template-grid">
          <div
            v-for="template in workflowTemplates"
            :key="template.id"
            class="template-option"
            :class="{
              'selected': selectedTemplate === template.id,
              'disabled': isTemplateDisabled(template)
            }"
            @click="!isTemplateDisabled(template) && selectTemplate(template)"
            :title="getTemplateDisabledReason(template) === 'coming-soon' ? 'This trigger is coming soon' : (getTemplateDisabledReason(template) === 'trigger-in-use' ? 'A workflow with this trigger already exists. Edit the existing workflow to add more actions.' : '')"
          >
            <div class="template-option-icon" :style="{ backgroundColor: template.color + '15' }">
              <i :class="template.icon" :style="{ color: template.color }"></i>
            </div>
            <div class="template-option-content">
              <span class="template-option-name">{{ template.name }}</span>
              <span class="template-option-desc">{{ template.description }}</span>
              <span v-if="getTemplateDisabledReason(template) === 'coming-soon'" class="template-coming-soon-badge">
                <i class="fa fa-clock-o"></i> Coming Soon
              </span>
              <span v-else-if="getTemplateDisabledReason(template) === 'trigger-in-use'" class="template-used-badge">
                <i class="fa fa-info-circle"></i> Workflow exists - edit to add actions
              </span>
            </div>
          </div>
        </div>
      </div>

      <div slot="modal-footer" class="modal-footer-btns">
        <b-button variant="outline-secondary" @click="hideCreateModal">Cancel</b-button>
        <b-button variant="success" class="create-template-btn" @click="createFromTemplate" :disabled="!selectedTemplate || !hasAvailableTemplates">
          Create from Template
        </b-button>
        <b-button variant="outline-success" @click="gotoCreatePage()">
          <i class="fa fa-plus"></i> Start from Scratch
        </b-button>
      </div>
    </b-modal>

    <!-- Delete Modal -->
    <b-modal ref="delete" id="delete" title="Delete Workflow" body-class="bg-white" centered>
      <div class="delete-modal-content">
        <i class="fa fa-exclamation-triangle warning-icon"></i>
        <p>Are you sure you want to delete this workflow?</p>
        <p class="warning-text">This action cannot be undone.</p>
      </div>
      <div slot="modal-footer" class="modal-footer-btns">
        <b-button variant="outline-secondary" @click="hideModal('delete')">Cancel</b-button>
        <b-button variant="danger" @click="del">Delete</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable */

import { mapActions } from 'vuex';
import WorkflowListSkeleton from './WorkflowListSkeleton.vue';
import WorkflowPreviewIllustration from './components/WorkflowPreviewIllustration.vue';
import mixin from '../../mixin.js';

// Human-readable trigger display map
const TRIGGER_MAP = {
  'APPOINTMENT_SCHEDULED': { label: 'Appointment Scheduled', icon: 'fa-calendar-check-o', color: '#6f42c1' },
  'APPOINTMENT_BROKEN':    { label: 'Appointment Broken',    icon: 'fa-calendar-times-o', color: '#dc3545' },
  'APPOINTMENT_COMPLETED': { label: 'Appointment Completed', icon: 'fa-calendar',          color: '#28a745' },
  'MANGO_VOICE':           { label: 'Missed Call',           icon: 'fa-phone',             color: '#fd7e14' },
  'PROCEDURE_ADDED_VIA_PROCS_QUICK_ADD': { label: 'Procedure added via Procs Quick Add', icon: 'fa-plus-square-o', color: '#0284c7' },
  'NEW_PATIENT':           { label: 'New Patient',           icon: 'fa-user-plus',         color: '#ffc107' },
  'X_FOLLOWUPS':           { label: 'Follow-ups Completed',  icon: 'fa-tasks',             color: '#217CE8' },
  'PRE_AUTH_RECEIVED':     { label: 'Pre-Auth Received',     icon: 'fa-check-circle',      color: '#20c997' },
  'WEBSITE_FORM':          { label: 'Website Form',          icon: 'fa-globe',             color: '#6610f2' },
  'FACEBOOK_LEAD':         { label: 'Facebook Lead',         icon: 'fa-facebook-square',   color: '#1877f2' },
  'GOOGLE_LEAD':           { label: 'Google Lead',           icon: 'fa-google',            color: '#ea4335' },
};

// Human-readable action display map
const ACTION_MAP = {
  'sms':             { label: 'SMS',   icon: 'fa-comment' },
  'email':           { label: 'Email', icon: 'fa-envelope' },
  'assigntask':      { label: 'Task',  icon: 'fa-tasks' },
  'sendform':        { label: 'Form',  icon: 'fa-file-text-o' },
  'popup':           { label: 'Popup', icon: 'fa-window-restore' },
  'mailletter':      { label: 'Mail',  icon: 'fa-envelope-o' },
  'wait':            { label: 'Wait',  icon: 'fa-clock-o' },
  'slack':           { label: 'Slack', icon: 'fa-slack' },
  'prospectivelead': { label: 'Lead Follow-up', icon: 'fa-user-plus' },
  'kanban':          { label: 'Kanban', icon: 'fa-columns' },
};

// Auto-generated descriptions by trigger type
const TRIGGER_DESCRIPTIONS = {
  'APPOINTMENT_SCHEDULED': 'Runs automatically when a new appointment is booked for a patient.',
  'APPOINTMENT_BROKEN':    'Runs automatically when a patient cancels or breaks their appointment.',
  'APPOINTMENT_COMPLETED': 'Runs automatically after a patient completes their appointment.',
  'MANGO_VOICE':           'Runs automatically when an incoming call is missed at the office.',
  'PROCEDURE_ADDED_VIA_PROCS_QUICK_ADD': 'Runs automatically when a procedure is added via Account → Procedures → Quick Add in Open Dental.',
  'NEW_PATIENT':           'Runs automatically when a new patient is added to the system.',
  'X_FOLLOWUPS':           'Runs automatically when a set number of follow-ups are completed.',
};

const SUGGESTIONS_STORAGE_KEY = 'teamio_workflow_suggestions_collapsed';

export default {
  name: "WorkflowList",
  components: { WorkflowListSkeleton, WorkflowPreviewIllustration },
  mixins: [mixin],

  created() {
    if(this.allSettings) {
      this.init();
    }
    // Restore suggestions collapsed state from localStorage
    try {
      this.suggestionsExpanded = localStorage.getItem(SUGGESTIONS_STORAGE_KEY) !== 'true';
    } catch(e) {
      this.suggestionsExpanded = true;
    }
  },

  data() {
    return {
      showOverlay: false,
      workflows: [],
      selectedWorkflowID: null,
      processingWorkflowId: null,
      searchTerm: '',
      selectedTrigger: null,
      statusFilter: null,
      sortField: 'LastUpdated',
      sortDirection: 'desc',
      currentPage: 1,
      itemsPerPage: 10,
      selectedTemplate: null,
      searchDebounceTimer: null,
      suggestionsExpanded: true,
      workflowTemplates: [
        {
          id: 'broken-apt-alert',
          name: 'Broken Appointment Alert',
          description: 'Get notified when a patient cancels',
          icon: 'fa fa-calendar-times-o',
          color: '#dc3545',
          trigger: 'appointmentbroken',
          triggerDisplay: 'APPOINTMENT_BROKEN',
          triggerEnabled: true,
          action: 'assigntask',
          workflowName: 'Broken Appointment Alert'
        },
        {
          id: 'followup-form',
          name: 'Follow-up Form',
          description: 'Send a form after follow-up is created',
          icon: 'fa fa-file-text-o',
          color: '#217CE8',
          trigger: 'xfollowups',
          triggerDisplay: 'X_FOLLOWUPS',
          triggerEnabled: false,
          action: 'sendform',
          workflowName: 'Follow-up Form Workflow'
        },
        {
          id: 'followup-task',
          name: 'Follow-up Task',
          description: 'Create a task when follow-up is added',
          icon: 'fa fa-tasks',
          color: '#28a745',
          trigger: 'xfollowups',
          triggerDisplay: 'X_FOLLOWUPS',
          triggerEnabled: false,
          action: 'assigntask',
          workflowName: 'Follow-up Task Workflow'
        },
        {
          id: 'patient-intake',
          name: 'Patient Intake',
          description: 'Send intake forms to new patients',
          icon: 'fa fa-user-plus',
          color: '#ffc107',
          trigger: 'newpatient',
          triggerDisplay: 'NEW_PATIENT',
          triggerEnabled: false,
          action: 'sendform',
          workflowName: 'Patient Intake Workflow'
        },
        {
          id: 'missed-call-followup',
          name: 'Missed Call Follow-up',
          description: 'Assign a task when a call is missed',
          icon: 'fa fa-phone',
          color: '#fd7e14',
          trigger: 'mangovoice',
          triggerDisplay: 'MANGO_VOICE',
          triggerEnabled: true,
          action: 'assigntask',
          workflowName: 'Missed Call Follow-up'
        }
      ]
    };
  },

  computed: {
    canEditWorkflow() {
      return this.$can('update', 'workflow');
    },

    enabled() {
      return this.workflows.map(w => w.Active);
    },

    activeCount() {
      return this.workflows.filter(w => w.Active).length;
    },

    inactiveCount() {
      return this.workflows.filter(w => !w.Active).length;
    },

    availableTriggers() {
      if (!this.workflows || this.workflows.length === 0) return [];
      const triggers = [...new Set(this.workflows.map(w => w.TriggerOn))];
      return triggers.filter(t => t);
    },

    usedTriggers() {
      if (!this.workflows || this.workflows.length === 0) return [];
      return [...new Set(
        this.workflows
          .map(w => w.TriggerOn)
          .filter(t => t)
          .map(t => this.normalizeTrigger(t))
      )];
    },

    unusedTemplates() {
      return this.workflowTemplates.filter(template => {
        const normalizedDisplay = this.normalizeTrigger(template.triggerDisplay);
        return !this.usedTriggers.includes(normalizedDisplay);
      });
    },

    isTemplateTriggerUsed() {
      return (template) => {
        const normalizedTemplateDisplay = this.normalizeTrigger(template.triggerDisplay);
        return this.usedTriggers.includes(normalizedTemplateDisplay);
      };
    },

    isTemplateDisabled() {
      return (template) => {
        if (!template.triggerEnabled) return true;
        const normalizedTemplateDisplay = this.normalizeTrigger(template.triggerDisplay);
        return this.usedTriggers.includes(normalizedTemplateDisplay);
      };
    },

    getTemplateDisabledReason() {
      return (template) => {
        if (!template.triggerEnabled) return 'coming-soon';
        const normalizedTemplateDisplay = this.normalizeTrigger(template.triggerDisplay);
        if (this.usedTriggers.includes(normalizedTemplateDisplay)) return 'trigger-in-use';
        return null;
      };
    },

    hasAvailableTemplates() {
      return this.workflowTemplates.some(template => !this.isTemplateDisabled(template));
    },

    // Mango Voice ("Missed Call") needs a MANGO_VOICE_SECURITY_TOKEN configured for the
    // site before it can work — otherwise a workflow built on it can never fire. Gate the
    // "Missed Call Follow-up" quick-start on it (mirrors WorkflowBuilder). Tulip Tree, 2026-07-18.
    mangoVoiceEnabled() {
      if (!this.allSettings) return false;
      const token = this.allSettings.MANGO_VOICE_SECURITY_TOKEN;
      return token && token.PropertyValue && token.PropertyValue.trim() !== '';
    },

    filteredWorkflows() {
      let result = this.workflows || [];

      // Filter by status
      if (this.statusFilter === 'active') {
        result = result.filter(w => w.Active);
      } else if (this.statusFilter === 'inactive') {
        result = result.filter(w => !w.Active);
      }

      // Filter by search term
      if (this.searchTerm) {
        const term = this.searchTerm.toLowerCase();
        result = result.filter(w =>
          (w.Name && w.Name.toLowerCase().includes(term)) ||
          (w.TriggerOn && w.TriggerOn.toLowerCase().includes(term)) ||
          (w.TriggerOn && this.getTriggerLabel(w.TriggerOn).toLowerCase().includes(term))
        );
      }

      // Filter by trigger
      if (this.selectedTrigger) {
        result = result.filter(w => w.TriggerOn === this.selectedTrigger);
      }

      // Sort
      if (this.sortField) {
        result = [...result].sort((a, b) => {
          let aVal = a[this.sortField];
          let bVal = b[this.sortField];

          if (this.sortField === 'CreatedOn' || this.sortField === 'LastUpdated') {
            aVal = new Date(aVal || 0);
            bVal = new Date(bVal || 0);
          }

          if (typeof aVal === 'string') {
            aVal = aVal.toLowerCase();
            bVal = (bVal || '').toLowerCase();
          }

          if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
          if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
          return 0;
        });
      }

      return result;
    },

    paginatedWorkflows() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredWorkflows.slice(start, end);
    },

    totalItems() {
      return this.filteredWorkflows.length;
    },

    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },

    startItem() {
      if (this.totalItems === 0) return 0;
      return ((this.currentPage - 1) * this.itemsPerPage) + 1;
    },

    endItem() {
      return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    }
  },

  methods: {
    ...mapActions('workflow', ['getAllWorkflows', 'deleteWorkflow', 'enableWorkflow', 'disableWorkflow']),

    async init() {
      this.showOverlay = true;
      try {
        if(this.workflows.length === 0) {
          this.workflows = await this.getAllWorkflows({siteID: this.siteID});
          if(this.workflows && Array.isArray(this.workflows)) {
            this.workflows.map(w => w.Active = Boolean(w.Active));
          }
        }
      } catch(err) {
        console.error('Error loading workflows:', err);
      } finally {
        this.showOverlay = false;
      }
    },

    // --- Display helpers ---

    getTriggerLabel(triggerOn) {
      const info = TRIGGER_MAP[triggerOn];
      return info ? info.label : triggerOn;
    },

    getTriggerIcon(triggerOn) {
      const info = TRIGGER_MAP[triggerOn];
      return info ? info.icon : 'fa-sitemap';
    },

    getTriggerColor(triggerOn) {
      const info = TRIGGER_MAP[triggerOn];
      return info ? info.color : '#64748b';
    },

    getFlowSummary(workflow) {
      const triggerLabel = this.getTriggerLabel(workflow.TriggerOn);
      const actions = this.parseActionsFromFlowyText(workflow.FlowyText);
      if (actions.length === 0) return triggerLabel;
      const actionLabels = actions.map(a => {
        const info = ACTION_MAP[a];
        return info ? info.label : a;
      });
      return triggerLabel + ' \u2192 ' + actionLabels.join(' + ');
    },

    getWorkflowDescription(workflow) {
      const desc = TRIGGER_DESCRIPTIONS[workflow.TriggerOn];
      return desc || 'Runs automatically based on configured triggers and actions.';
    },

    parseActionsFromFlowyText(flowyText) {
      if (!flowyText) return [];
      try {
        const parsed = typeof flowyText === 'string' ? JSON.parse(flowyText) : flowyText;
        const blocks = parsed.blocks || parsed;
        if (!Array.isArray(blocks)) return [];

        const actions = [];
        for (const block of blocks) {
          if (block.parent === -1) continue; // skip trigger node
          const dataArr = block.data || [];
          for (const d of dataArr) {
            if (d.name === 'blockelemtype' && d.value) {
              actions.push(d.value);
              break;
            }
          }
        }
        return [...new Set(actions)]; // deduplicate
      } catch(e) {
        return [];
      }
    },

    // --- Filtering ---

    toggleStatusFilter(status) {
      this.statusFilter = this.statusFilter === status ? null : status;
      this.currentPage = 1;
    },

    // --- Suggestions ---

    toggleSuggestions() {
      this.suggestionsExpanded = !this.suggestionsExpanded;
      try {
        localStorage.setItem(SUGGESTIONS_STORAGE_KEY, this.suggestionsExpanded ? 'false' : 'true');
      } catch(e) { /* ignore */ }
    },

    createFromTemplateDirectly(template) {
      let routeData = this.$router.resolve({
        name: 'CreateWorkflow2',
        query: {
          template: template.id,
          trigger: template.trigger,
          action: template.action,
          name: template.workflowName
        }
      });
      window.open(routeData.href, '_blank');
    },

    // --- Existing methods ---

    normalizeTrigger(trigger) {
      if (!trigger) return '';
      return trigger
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/-/g, '_');
    },

    showModal(name, workflowID) {
      // Defense-in-depth: button is v-if'd by $can('delete','workflow') but
      // also gate the handler in case the v-if is stale (rules changed mid-session).
      if (name === 'delete' && !this.assertCan('delete', 'workflow')) return;
      this.selectedWorkflowID = workflowID;
      this.$refs[name].show();
    },

    hideModal(ref) {
      this.$refs[ref].hide();
    },

    showCreateModal() {
      if (!this.assertCan('update', 'workflow')) return;
      this.selectedTemplate = null;
      this.$refs.createWorkflowModal.show();
    },

    hideCreateModal() {
      this.$refs.createWorkflowModal.hide();
      this.selectedTemplate = null;
    },

    selectTemplate(template) {
      this.selectedTemplate = template.id;
    },

    createFromTemplate() {
      if (!this.assertCan('update', 'workflow')) return;
      if (!this.selectedTemplate) return;

      const template = this.workflowTemplates.find(t => t.id === this.selectedTemplate);
      if (template) {
        if (this.isTemplateDisabled(template)) {
          const reason = this.getTemplateDisabledReason(template);
          if (reason === 'coming-soon') {
            this.$toast.error('This trigger is coming soon');
          } else {
            this.$toast.error('A workflow with this trigger already exists');
          }
          return;
        }

        let routeData = this.$router.resolve({
          name: 'CreateWorkflow2',
          query: {
            template: template.id,
            trigger: template.trigger,
            action: template.action,
            name: template.workflowName
          }
        });
        window.open(routeData.href, '_blank');
        this.hideCreateModal();
      }
    },

    gotoCreatePage() {
      if (!this.assertCan('update', 'workflow')) return;
      let routeData = this.$router.resolve({name: 'CreateWorkflow2'});
      window.open(routeData.href, '_blank');
      if (this.$refs.createWorkflowModal) {
        this.hideCreateModal();
      }
    },

    gotoWorkflowOutputPage(workflowID) {
      let routeData = this.$router.resolve({name: 'WorkflowOutput2', params: {workflowID: `${workflowID}`}});
      window.open(routeData.href, '_blank');
    },

    gotoViewPage(workflowID) {
      // Silent guard — card click is gated by canEditWorkflow in the template
      // and the pencil icon is hidden by v-if. No toast: this path is not
      // reachable via UI for users without 'update' permission.
      if (!this.$can('update', 'workflow')) return;
      let routeData = this.$router.resolve({name: 'ViewWorkflow2', params: {workflowID: `${workflowID}`}});
      window.open(routeData.href, '_blank');
    },

    async del() {
      if (!this.assertCan('delete', 'workflow')) return;
      try {
        this.hideModal('delete');
        this.showOverlay = true;
        await this.deleteWorkflow({WorkflowID: this.selectedWorkflowID});
        this.showOverlay = false;
        this.$toast.success("Workflow deleted successfully!");
        this.$router.go(0);
      } catch(e) {
        this.showOverlay = false;
        console.error('Error deleting workflow:', e);
        this.$toast.error("Error deleting workflow. Please try again later");
      }
    },

    async toggleWorkflowActive(workflow) {
      if(this.processingWorkflowId === workflow.WorkflowID) {
        return;
      }

      this.processingWorkflowId = workflow.WorkflowID;

      try {
        if(workflow.Active) {
          await this.enableWorkflow({WorkflowID: workflow.WorkflowID});
        } else {
          await this.disableWorkflow({WorkflowID: workflow.WorkflowID});
        }
      } catch(err) {
        console.error(`Error toggling workflow:`, err);
        workflow.Active = !workflow.Active;
        this.$toast.error("Error updating workflow status");
      } finally {
        this.processingWorkflowId = null;
      }
    },

    onSearchInput() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.currentPage = 1;
      }, 300);
    },

    clearSearch() {
      this.searchTerm = '';
      this.currentPage = 1;
    },

    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
      this.currentPage = 1;
    },

    getSortIcon(field) {
      if (this.sortField !== field) {
        return 'fa fa-sort';
      }
      return this.sortDirection === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc';
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    }
  },

  watch: {
    allSettings(newValue, oldValue) {
      if(!oldValue) {
        this.init();
      }
    },

    // Keep the Missed Call (Mango Voice) quick-start's triggerEnabled in sync with whether
    // the site has Mango Voice set up. Fail-safe: disabled until the token is confirmed, so
    // a site can't start a workflow that could never fire. (Tulip Tree, 2026-07-18.)
    mangoVoiceEnabled: {
      immediate: true,
      handler(val) {
        const mango = this.workflowTemplates.find(t => t.trigger === 'mangovoice');
        if (mango) mango.triggerEnabled = !!val;
      },
    },

    enabled: {
      async handler(newValue, oldValue) {
        if(oldValue.length) {
          const indexOfChangedWorkflow = newValue.findIndex((v, i) => v !== oldValue[i]);
          if(indexOfChangedWorkflow >= 0) {
            let workflowChanged = this.workflows[indexOfChangedWorkflow];
            await this.toggleWorkflowActive(workflowChanged);
          }
        }
      }
    }
  },

  beforeDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  }
};
</script>

<style src="./WorkflowList.scss" lang="scss" scoped></style>
